import { Flex, type ConnectionResourceItemProps } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import {
  useBankConnectFactory,
  useBankConnectionLogoResolvers,
  useBankConnectionLogos,
  useBankConnectWithBasiq,
} from "@wayflyer/customer-bankconnect";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { AuthCallbackPath } from "shared-ui";
import { useMount } from "shared-utils";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { useAirReconnectConnectionMutation } from "../../../queries/air-response/air-reconnect-connection.mutations";
import { useBankConnectProps } from "../../../queries/bank-connect/queries";
import { usePlatformConnectors } from "../../../queries/connectors/queries";
import { connectedBankingDataSourcesItemProps, connectedConnectorResourcesItemProps } from "../components";
import { PlatformsAccordion } from "../components/platforms-accordion";

import { BankStatementUpload } from "./bank-statement-upload/bank-statement-upload";
import { CannotReconnectBank } from "./bank-statement-upload/cannot-reconnect-bank";
import { ReconnectLanding } from "./reconnect-landing/reconnect-landing";
import { type ReconnectionStep } from "./util";

interface ReconnectConnectionProps {
  onBack: () => void;
  airPublicId: string;
  airData: FundingApplicationSchemas.ReconnectConnectionAIRSchema;
}

export const ReconnectConnection = ({ airPublicId, onBack, airData }: ReconnectConnectionProps) => {
  const intl = useIntl();

  const [searchParams, setSearchParams] = useSearchParams();
  const currentReconnectStep = searchParams.get("step");
  const oauthError = searchParams.get("oauth-error");
  const connectionInProgress = searchParams.get("platform");

  const setCurrentReconnectStep = (step: ReconnectionStep, connector?: string) => {
    if (connector) {
      setSearchParams({ step, platform: connector });
      return;
    }
    setSearchParams({ step });
  };

  useMount(() => {
    if (!currentReconnectStep) {
      const params: { [key: string]: string } = {
        step: "reconnect_connection_landing",
      };
      if (connectionInProgress) {
        params["platform"] = connectionInProgress;
        if (oauthError) {
          params["oauth-error"] = oauthError;
        }
      }
      setSearchParams(params, { replace: true }); // Replace the current history entry
    }
  });

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: connectors } = usePlatformConnectors(customerDomain, {
    skipConnections: false,
  });
  const updateReconnectConnectionAIR = useAirReconnectConnectionMutation(customerDomain);

  const {
    data: { bankConnectProps },
  } = useBankConnectProps(customerDomain);
  const { useBankConnect } = useBankConnectFactory(bankConnectProps, {
    customerSlug: customerDomain,
    basiqNextPath: AuthCallbackPath.FUNDING_APPLICATION_BANKING,
    onErrorOrAbandon() {
      setCurrentReconnectStep("cannot_reconnect_bank");
    },
    onReconnectionCallback() {
      updateReconnectConnectionAIR
        .mutateAsync({
          airPublicId,
          reconnectedConnectionId: airData.connection_id,
        })
        .then(() => {
          onBack();
        });
    },
  });
  const { bankConnections } = useBankConnect();

  const { basiqConnectProps } = bankConnectProps;
  const { basiqClient } = useBankConnectWithBasiq({
    ...basiqConnectProps,
    customerSlug: customerDomain,
    nextPath: AuthCallbackPath.FUNDING_APPLICATION_BANKING,
  });

  const logoResolvers = useBankConnectionLogoResolvers({ customerSlug: customerDomain, basiqClient });
  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections,
    resolvers: logoResolvers,
  });

  const connectedConnectionItems = connectedConnectorResourcesItemProps(intl, connectors);
  const connectedBankItems = connectedBankingDataSourcesItemProps(intl, logoLoadingStates, bankConnections);
  const connectedItems: ConnectionResourceItemProps[] = [...connectedConnectionItems, ...connectedBankItems];

  return (
    <Flex direction="column" gap="8">
      {currentReconnectStep === "reconnect_connection_landing" && (
        <ReconnectLanding
          airData={airData}
          useBankConnect={useBankConnect}
          onBack={onBack}
          setCurrentReconnectStep={setCurrentReconnectStep}
        />
      )}
      {currentReconnectStep === "cannot_reconnect_bank" && (
        <CannotReconnectBank setCurrentReconnectStep={setCurrentReconnectStep} />
      )}
      {currentReconnectStep === "upload_bank_statements" && (
        <BankStatementUpload
          airPublicId={airPublicId}
          bankName={airData.connection_identifier}
          bankAccountMasks={
            airData.connection_meta_data?.type === "BANKING" ? airData.connection_meta_data.account_masks : []
          }
          customerDomain={customerDomain}
          onSubmit={onBack}
          onBack={() => setCurrentReconnectStep("cannot_reconnect_bank")}
        />
      )}
      {currentReconnectStep !== "upload_bank_statements" && (
        <PlatformsAccordion
          header={intl.formatMessage({
            id: "funding-application: air reconnect platforms accordion title",
            defaultMessage: "View your connected platforms",
          })}
          platformItems={connectedItems}
        />
      )}
    </Flex>
  );
};
